<template>
  <nav class="bg-slate-900 backdrop-blur-md border-b border-cyan-400/20 fixed top-0 left-0 right-0 z-50 shadow-[0_0_20px_rgba(34,211,238,0.12),0_12px_36px_-8px_rgba(34,211,238,0.14)]">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between items-center h-20">
        <button
          type="button"
          @click="navigate('agent-dashboard')"
          class="group flex shrink-0 items-center self-center cursor-pointer min-w-0 mr-2 sm:mr-3"
        >
          <img
            :src="logoUrl"
            alt="PowerAgent"
            class="block h-auto w-auto max-h-14 max-w-[min(100%,15rem)] object-contain object-left sm:max-h-16 md:max-h-20 md:max-w-none"
          />
        </button>

        <div class="flex items-center lg:hidden">
          <button
            type="button"
            @click="toggleMobileMenu"
            class="p-2 rounded-lg text-slate-300 hover:bg-cyan-400/10 hover:text-cyan-300 transition"
            aria-label="Abrir menu"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        <div class="hidden lg:flex items-center space-x-1 flex-1 justify-center">
          <button
            type="button"
            :aria-current="router.currentRoute === 'users' ? 'page' : undefined"
            @click="navigate('users')"
            :class="navClass('users', 'desktop')"
          >
            Usuários
          </button>
          <div ref="agentesMenuRoot" class="relative">
            <button
              type="button"
              :aria-expanded="isAgentesMenuOpen ? 'true' : 'false'"
              :aria-current="agentesMenuActive ? 'page' : undefined"
              @click.stop="toggleAgentesMenu"
              :class="navClass('agentes-menu', 'desktop')"
            >
              Agentes
              <svg
                class="inline-block w-4 h-4 ml-1 transition-transform"
                :class="{ 'rotate-180': isAgentesMenuOpen }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <div
                v-if="isAgentesMenuOpen"
                class="absolute left-0 mt-2 w-64 bg-slate-900 backdrop-blur-md rounded-lg shadow-xl shadow-black/40 py-2 z-20 border border-cyan-400/20"
              >
                <button
                  type="button"
                  :aria-current="router.currentRoute === 'agents-executors' ? 'page' : undefined"
                  @click="navigate('agents-executors')"
                  :class="navClass('agents-executors', 'dropdown')"
                >
                  Executores
                </button>
                <button
                  type="button"
                  :aria-current="router.currentRoute === 'agents-coordinators' ? 'page' : undefined"
                  @click="navigate('agents-coordinators')"
                  :class="navClass('agents-coordinators', 'dropdown')"
                >
                  Coordenadores
                </button>
                <button
                  type="button"
                  :aria-current="router.currentRoute === 'coordinator-groups' ? 'page' : undefined"
                  @click="navigate('coordinator-groups')"
                  :class="navClass('coordinator-groups', 'dropdown')"
                >
                  Grupos de Coordenadores
                </button>
                <div class="border-t border-slate-700/80 my-1 mx-2"></div>
                <button
                  type="button"
                  :aria-current="router.currentRoute === 'agents-schedules' ? 'page' : undefined"
                  @click="navigate('agents-schedules')"
                  :class="navClass('agents-schedules', 'dropdown')"
                >
                  Agendamentos Cadastrados
                </button>                
                <button
                  type="button"
                  :aria-current="router.currentRoute === 'agents-network' ? 'page' : undefined"
                  @click="navigate('agents-network')"
                  :class="navClass('agents-network', 'dropdown')"
                >
                  Redes de Agentes
                </button>
              </div>
            </transition>
          </div>
          <button
            type="button"
            :aria-current="router.currentRoute === 'agent-dashboard' ? 'page' : undefined"
            @click="navigate('agent-dashboard')"
            :class="navClass('agent-dashboard', 'desktop')"
          >
            Painel
          </button>
          <button
            type="button"
            :aria-current="router.currentRoute === 'coordinator-chat' ? 'page' : undefined"
            @click="navigate('coordinator-chat')"
            :class="navClass('coordinator-chat', 'desktop')"
          >
            Chat
          </button>

          <div ref="paramMenuRoot" class="relative">
            <button
              type="button"
              :aria-expanded="isParamMenuOpen ? 'true' : 'false'"
              :aria-current="paramMenuActive ? 'page' : undefined"
              @click.stop="toggleParamMenu"
              :class="navClass('param-menu', 'desktop')"
            >
              Conectores
              <svg
                class="inline-block w-4 h-4 ml-1 transition-transform"
                :class="{ 'rotate-180': isParamMenuOpen }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <div
                v-if="isParamMenuOpen"
                class="absolute left-0 mt-2 w-56 bg-slate-900 backdrop-blur-md rounded-lg shadow-xl shadow-black/40 py-2 z-20 border border-cyan-400/20"
              >
                <button
                  type="button"
                  :aria-current="router.currentRoute === 'api-connectors' ? 'page' : undefined"
                  @click="navigate('api-connectors')"
                  :class="navClass('api-connectors', 'dropdown')"
                >
                  APIs e Endpoints
                </button>
                <button
                  type="button"
                  :aria-current="router.currentRoute === 'database-connectors' ? 'page' : undefined"
                  @click="navigate('database-connectors')"
                  :class="navClass('database-connectors', 'dropdown')"
                >
                  Banco de Dados
                </button>
                <button
                  type="button"
                  :aria-current="router.currentRoute === 'email-delivery-connectors' ? 'page' : undefined"
                  @click="navigate('email-delivery-connectors')"
                  :class="navClass('email-delivery-connectors', 'dropdown')"
                >
                  E-mail de envio (Saída)
                </button>
                <button
                  type="button"
                  :aria-current="router.currentRoute === 'email-inbox-connectors' ? 'page' : undefined"
                  @click="navigate('email-inbox-connectors')"
                  :class="navClass('email-inbox-connectors', 'dropdown')"
                >
                  Caixas de Email (entrada)
                </button>
                <button
                  type="button"
                  :aria-current="router.currentRoute === 'whatsapp-delivery-connectors' ? 'page' : undefined"
                  @click="navigate('whatsapp-delivery-connectors')"
                  :class="navClass('whatsapp-delivery-connectors', 'dropdown')"
                >
                  WhatsApp (envio)
                </button>
              </div>
            </transition>
          </div>
        </div>

        <div class="hidden lg:flex items-center space-x-4">
          <div class="relative">
            <button
              type="button"
              @click="toggleDropdown"
              class="flex items-center space-x-2 text-slate-200 hover:text-cyan-300 focus:outline-none transition"
            >
              <div class="w-12 h-12 rounded-full overflow-hidden border-2 border-cyan-400/50 bg-gradient-to-br from-cyan-500 to-cyan-400 flex items-center justify-center text-slate-950 font-semibold text-sm shadow-[0_0_14px_rgba(34,211,238,0.28)]">
                <img
                  v-if="userAvatar"
                  :src="userAvatar"
                  alt="Avatar"
                  class="w-full h-full object-cover"
                  style="min-width:100%;min-height:100%;object-fit:cover;object-position:center;"
                />
                <span v-else>{{ userInitials }}</span>
              </div>
              <span class="hidden sm:block text-sm font-medium truncate max-w-xs">{{ userName }}</span>
              <svg
                class="w-4 h-4 transition-transform hidden sm:block"
                :class="{ 'rotate-180': isDropdownOpen }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <div
                v-if="isDropdownOpen"
                class="absolute right-0 mt-2 w-48 bg-slate-900 backdrop-blur-md rounded-lg shadow-xl shadow-black/40 py-2 z-10 border border-cyan-400/20"
              >
                <button
                  type="button"
                  :aria-current="router.currentRoute === 'profile' ? 'page' : undefined"
                  @click="navigate('profile')"
                  :class="navClass('profile', 'dropdown')"
                >
                  <svg class="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>Seu Perfil</span>
                </button>
                <button
                  type="button"
                  :aria-current="router.currentRoute === 'tenant-settings' ? 'page' : undefined"
                  @click="navigate('tenant-settings')"
                  :class="navClass('tenant-settings', 'dropdown')"
                >
                  <svg class="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>Ambiente</span>
                </button>
                <div class="border-t border-slate-700/80 my-1"></div>
                <button
                  type="button"
                  @click="logout"
                  class="w-full text-left flex items-center px-4 py-2 text-sm text-red-300 hover:bg-red-500/10 transition"
                >
                  <svg class="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>Sair</span>
                </button>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="isMobileMenuOpen"
      ref="mobileMenuOverlay"
      class="lg:hidden fixed inset-0 z-[70]"
    >
      <div class="absolute inset-0 bg-black bg-opacity-30" @click="isMobileMenuOpen = false"></div>
      <div
        class="absolute top-20 left-0 right-0 bg-slate-900/95 backdrop-blur-md border-t border-cyan-400/20 max-h-[calc(100vh-5rem)] overflow-y-auto"
      >
        <div class="px-4 pt-2 pb-3 space-y-1">
          <button
            type="button"
            :aria-current="router.currentRoute === 'users' ? 'page' : undefined"
            @click="navigate('users')"
            :class="navClass('users', 'mobile')"
          >
            Usuários
          </button>
          <p class="px-3 pt-2 pb-0 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Agentes</p>
          <button
            type="button"
            :aria-current="router.currentRoute === 'agents-executors' ? 'page' : undefined"
            @click="navigate('agents-executors')"
            :class="navClass('agents-executors', 'mobile')"
          >
            Executores
          </button>
          <button
            type="button"
            :aria-current="router.currentRoute === 'agents-coordinators' ? 'page' : undefined"
            @click="navigate('agents-coordinators')"
            :class="navClass('agents-coordinators', 'mobile')"
          >
            Coordenadores
          </button>
          <button
            type="button"
            :aria-current="router.currentRoute === 'coordinator-groups' ? 'page' : undefined"
            @click="navigate('coordinator-groups')"
            :class="navClass('coordinator-groups', 'mobile')"
          >
            Grupos de Coordenadores
          </button>
          <div class="border-t border-slate-700/80 my-1 mx-3"></div>
          <button
            type="button"
            :aria-current="router.currentRoute === 'agents-schedules' ? 'page' : undefined"
            @click="navigate('agents-schedules')"
            :class="navClass('agents-schedules', 'mobile')"
          >
            Agendamentos Cadastrados
          </button>          
          <button
            type="button"
            :aria-current="router.currentRoute === 'agents-network' ? 'page' : undefined"
            @click="navigate('agents-network')"
            :class="navClass('agents-network', 'mobile')"
          >
            Redes de Agentes
          </button>
          <button
            type="button"
            :aria-current="router.currentRoute === 'agent-dashboard' ? 'page' : undefined"
            @click="navigate('agent-dashboard')"
            :class="navClass('agent-dashboard', 'mobile')"
          >
            Painel
          </button>
          <button
            type="button"
            :aria-current="router.currentRoute === 'coordinator-chat' ? 'page' : undefined"
            @click="navigate('coordinator-chat')"
            :class="navClass('coordinator-chat', 'mobile')"
          >
            Chat
          </button>
          <p class="px-3 pt-2 pb-0 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Conectores</p>
          <button
            type="button"
            :aria-current="router.currentRoute === 'api-connectors' ? 'page' : undefined"
            @click="navigate('api-connectors')"
            :class="navClass('api-connectors', 'mobile')"
          >
            APIs e Endpoints
          </button>
          <button
            type="button"
            :aria-current="router.currentRoute === 'database-connectors' ? 'page' : undefined"
            @click="navigate('database-connectors')"
            :class="navClass('database-connectors', 'mobile')"
          >
            Banco de Dados
          </button>
          <button
            type="button"
            :aria-current="router.currentRoute === 'email-delivery-connectors' ? 'page' : undefined"
            @click="navigate('email-delivery-connectors')"
            :class="navClass('email-delivery-connectors', 'mobile')"
          >
            E-mail de envio (saída)
          </button>
          <button
            type="button"
            :aria-current="router.currentRoute === 'email-inbox-connectors' ? 'page' : undefined"
            @click="navigate('email-inbox-connectors')"
            :class="navClass('email-inbox-connectors', 'mobile')"
          >
            Caixas de Email (entrada)
          </button>
          <button
            type="button"
            :aria-current="router.currentRoute === 'whatsapp-delivery-connectors' ? 'page' : undefined"
            @click="navigate('whatsapp-delivery-connectors')"
            :class="navClass('whatsapp-delivery-connectors', 'mobile')"
          >
            WhatsApp (envio)
          </button>
          <button
            type="button"
            :aria-current="router.currentRoute === 'profile' ? 'page' : undefined"
            @click="navigate('profile')"
            :class="navClass('profile', 'mobile')"
          >
            Seu Perfil
          </button>
          <button
            type="button"
            :aria-current="router.currentRoute === 'tenant-settings' ? 'page' : undefined"
            @click="navigate('tenant-settings')"
            :class="navClass('tenant-settings', 'mobile')"
          >
            Ambiente
          </button>
          <button
            type="button"
            @click="logout"
            class="w-full text-left block text-red-300 hover:bg-red-500/10 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150"
          >
            Sair
          </button>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import { navigateTo, router } from '../router.js'

const NON_ADMIN_NAV_ROUTES = new Set([
  'agent-dashboard',
  'agents-network',
  'coordinator-chat',
  'profile'
])

export default {
  props: {
    user: {
      type: Object,
      required: true
    },
    tenantAdmin: {
      type: Boolean,
      required: true
    }
  },

  data() {
    return {
      router,
      logoUrl: '/poweragent-logo.png',
      isDropdownOpen: false,
      isMobileMenuOpen: false,
      isParamMenuOpen: false,
      isAgentesMenuOpen: false
    }
  },

  computed: {
    agentesMenuActive() {
      return (
        this.router.currentRoute === 'agents-coordinators' ||
        this.router.currentRoute === 'coordinator-groups' ||
        this.router.currentRoute === 'agents-executors' ||
        this.router.currentRoute === 'agents-network' ||
        this.router.currentRoute === 'agents-schedules'
      )
    },
    paramMenuActive() {
      return (
        this.router.currentRoute === 'api-connectors' ||
        this.router.currentRoute === 'database-connectors' ||
        this.router.currentRoute === 'email-delivery-connectors' ||
        this.router.currentRoute === 'email-inbox-connectors' ||
        this.router.currentRoute === 'whatsapp-delivery-connectors'
      )
    },
    userName() {
      if (this.user.first_name && this.user.last_name) {
        return `${this.user.first_name} ${this.user.last_name}`
      }
      return this.user.email
    },

    userInitials() {
      if (this.user.first_name && this.user.last_name) {
        return `${this.user.first_name[0]}${this.user.last_name[0]}`.toUpperCase()
      }
      return this.user.email[0]?.toUpperCase() || ''
    },

    userAvatar() {
      return this.user.profile_image || ''
    }
  },

  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },

  methods: {
    navRouteAllowed(route) {
      if (this.tenantAdmin) return true
      return NON_ADMIN_NAV_ROUTES.has(route)
    },

    navClass(route, variant = 'desktop') {
      let on = this.router.currentRoute === route
      if (route === 'param-menu') {
        on = this.paramMenuActive
      }
      if (route === 'agentes-menu') {
        on = this.agentesMenuActive
      }
      const blocked =
        route === 'param-menu'
          ? !this.tenantAdmin
          : route === 'agentes-menu'
            ? false
            : !this.navRouteAllowed(route)
      if (variant === 'dropdown') {
        const base = 'w-full text-left flex items-center px-4 py-2 text-sm rounded-lg transition-colors duration-150'
        if (blocked) {
          return `${base} text-slate-500 opacity-55 cursor-not-allowed border border-transparent pointer-events-none`
        }
        if (on) {
          return `${base} text-cyan-300 bg-cyan-400/15 border border-cyan-400/35`
        }
        return `${base} text-slate-200 border border-transparent hover:bg-cyan-400/10 hover:text-cyan-300`
      }
      const base =
        variant === 'mobile'
          ? 'w-full text-left block px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150'
          : 'px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-150'
      const idleText = variant === 'mobile' ? 'text-slate-200' : 'text-slate-300'
      if (blocked) {
        return `${base} text-slate-500 opacity-55 cursor-not-allowed border border-transparent pointer-events-none`
      }
      if (on) {
        return `${base} text-cyan-300 bg-cyan-400/15 border border-cyan-400/35 shadow-[0_0_12px_rgba(34,211,238,0.12)]`
      }
      return `${base} ${idleText} border border-transparent hover:text-cyan-300 hover:bg-cyan-400/10`
    },

    navigate(route) {
      if (!this.navRouteAllowed(route)) return
      navigateTo(route)
      this.isDropdownOpen = false
      this.isMobileMenuOpen = false
      this.isParamMenuOpen = false
      this.isAgentesMenuOpen = false
    },

    toggleAgentesMenu() {
      this.isAgentesMenuOpen = !this.isAgentesMenuOpen
      if (this.isAgentesMenuOpen) this.isParamMenuOpen = false
    },

    toggleParamMenu() {
      if (!this.tenantAdmin) return
      this.isParamMenuOpen = !this.isParamMenuOpen
      if (this.isParamMenuOpen) this.isAgentesMenuOpen = false
    },

    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen
    },

    toggleMobileMenu() {
      this.isMobileMenuOpen = !this.isMobileMenuOpen
    },

    handleClickOutside(event) {
      const navElement = this.$el
      const overlayEl = this.$refs.mobileMenuOverlay

      if (!navElement || navElement.contains(event.target)) {
        return
      }

      if (overlayEl && overlayEl.contains(event.target)) {
        return
      }

      this.isDropdownOpen = false
      this.isMobileMenuOpen = false
      this.isParamMenuOpen = false
      this.isAgentesMenuOpen = false
    },

    logout() {
      localStorage.removeItem('userToken')
      localStorage.removeItem('tenantToken')
      localStorage.removeItem('tenant')
      localStorage.removeItem('user')
      localStorage.removeItem('masterCode')
      navigateTo('dashboard', {})
      window.location.reload()
    }
  }
}
</script>
